import { CompanyTag } from "@/components/domain/CompanyTag";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronDown,
  Download,
  Eye,
  Printer,
  RotateCcw,
  Search,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type {
  StatementDto,
  StatementLineDto,
  StatementReportMeta,
} from "@hisense/shared";
import {
  useQuotationCustomerOptions,
  useStatementReport,
  useSettings,
} from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { ButtonRing } from "@/components/brand/ButtonRing";
import { Panel } from "@/components/ui/Panel";
import { Select } from "@/components/ui/Select";
import { SearchableFilter } from "@/components/ui/SearchableFilter";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { ErrorState, LoadingState, EmptyState } from "@/components/ui/States";
import { buildStatementHtml, printStatement } from "@/lib/printStatement";
import { exportStatementWorkbook } from "@/lib/statementSheets";
import { formatMoney } from "@/domain/money";
import { formatDate, formatDateTime } from "@/domain/datetime";
import { statementMonthOf, statementMonthOptions, statementMonthRange } from "@/domain/statementMonths";
import { SaveAsPdfHint } from "@/components/domain/SaveAsPdfHint";
import { useSearchDraft } from "@/lib/listQuery";

const FILTERS = [
  "all",
  "outstanding",
  "overdue",
  "settled",
  "credit",
  "review",
] as const;
const SORTS = ["date", "-date", "due", "-balance"] as const;
const BUCKETS = [
  "Current",
  "1–30",
  "31–60",
  "61–90",
  "90+",
  "Unknown",
] as const;
const BUCKET_KEYS = [
  "current",
  "days1to30",
  "days31to60",
  "days61to90",
  "days90plus",
  "unknown",
] as const;
const BUCKET_TONES = [
  "var(--color-ok)",
  "var(--color-warn)",
  "var(--color-warn)",
  "var(--color-danger)",
  "var(--color-danger)",
  "var(--color-ink-faint)",
];
const CURRENCIES = ["USD", "HKD", "CNY", "EUR", "GBP", "JPY", "VND"];
const groupKey = (group: StatementDto) =>
  JSON.stringify([group.customerId, group.currency]);

function dateValue(value: string, endOfDay = false): number | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (year == null || month == null || day == null) return undefined;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  )
    return undefined;
  if (endOfDay) date.setHours(23, 59, 59, 999);
  return date.getTime();
}

function Aging({
  groups,
  currency,
}: {
  groups: StatementDto[];
  currency: string;
}) {
  const { t } = useTranslation();
  const amounts = BUCKETS.map((bucket) =>
    groups.reduce(
      (sum, group) =>
        sum +
        (group.buckets.find((entry) => entry.bucket === bucket)?.amount ?? 0),
      0,
    ),
  );
  const max = Math.max(1, ...amounts);
  return (
    <div className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
      {BUCKETS.map((bucket, index) => (
        <div key={bucket} className="min-w-0">
          <div className="mb-1 flex items-baseline justify-between gap-2 text-xs text-ink-soft">
            <span>{t(`statementWorkspace.buckets.${BUCKET_KEYS[index]}`)}</span>
          </div>
          <div className="mb-2 font-mono tabular text-sm text-ink">
            {formatMoney(amounts[index] ?? 0, currency)}
          </div>
          <div
            className="h-1.5 overflow-hidden rounded-full bg-sunken"
            aria-hidden="true"
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${((amounts[index] ?? 0) / max) * 100}%`,
                backgroundColor: BUCKET_TONES[index],
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Settlement({ line }: { line: StatementLineDto }) {
  const { t } = useTranslation();
  const status = line.archived
    ? "archived"
    : line.balance < 0
      ? "credit"
      : line.balance === 0
        ? "settled"
        : line.paid > 0
          ? "partial"
          : "unpaid";
  const tone =
    status === "settled"
      ? "ok"
      : status === "credit"
        ? "info"
        : status === "partial"
          ? "warn"
          : "neutral";
  return (
    <span className="inline-flex items-center gap-1.5">
      <ButtonRing
        size={15}
        holes={2}
        tone={tone}
        progress={
          line.archived
            ? undefined
            : line.total > 0
              ? Math.min(1, Math.max(0, line.paid / line.total))
              : line.balance <= 0
                ? 1
                : 0
        }
      />
      <Badge tone={tone}>
        {t(
          status === "archived"
            ? "statementWorkspace.archived"
            : `statementWorkspace.settlement.${status}`,
        )}
      </Badge>
    </span>
  );
}

function StatementLines({
  group,
  showPeriod,
}: {
  group: StatementDto;
  showPeriod: boolean;
}) {
  const { t } = useTranslation();
  return (
    <TableShell className="rounded-none border-x-0 border-b-0">
      <thead>
        <tr>
          <Th pin>{t("pi.piNo")}</Th>
          <Th>{t("statementWorkspace.linkedInvoice")}</Th>
          <Th>{t("pi.date")}</Th>
          <Th>{t("deliveryNotes.deliveryDate")}</Th>
          <Th>{t("statementWorkspace.terms")}</Th>
          <Th>{t("statementWorkspace.dueDate")}</Th>
          <Th align="right">{t("pi.total")}</Th>
          <Th align="right">{t("pi.paid")}</Th>
          {showPeriod ? (
            <>
              <Th align="right">{t("statementWorkspace.opening")}</Th>
              <Th align="right">{t("statementWorkspace.periodBilled")}</Th>
              <Th align="right">{t("statementWorkspace.periodPaid")}</Th>
              <Th align="right">{t("statementWorkspace.adjustments")}</Th>
            </>
          ) : null}
          <Th align="right">{t("statementWorkspace.netBalance")}</Th>
          <Th>{t("statementWorkspace.settlementLabel")}</Th>
          <Th>{t("statementWorkspace.agingLabel")}</Th>
          <Th>{t("statementWorkspace.reviewLabel")}</Th>
        </tr>
      </thead>
      <tbody>
        {group.lines.map((line) => (
          <Tr key={line.piId}>
            <Td pin className="font-mono tabular font-medium">
              {line.archived || line.piArchived ? (
                <span className="text-ink-soft">
                  {line.piNo}
                  <span className="mt-1 block font-sans text-2xs">
                    {t("statementWorkspace.archived")}
                  </span>
                </span>
              ) : (
                <Link
                  className="text-brand underline-offset-4 hover:underline"
                  to={`/proforma-invoices/${line.piId}`}
                >
                  {line.piNo}
                </Link>
              )}
            </Td>
            <Td className="font-mono tabular text-xs">
              {line.invoiceId ? (
                <Link
                  className="text-brand underline-offset-4 hover:underline"
                  to={`/invoices/${line.invoiceId}${line.invoiceArchived ? "?archived=true" : ""}`}
                >
                  {line.invoiceNo ?? line.invoiceId}
                  {line.invoiceArchived ? (
                    <span className="mt-1 block font-sans text-2xs text-ink-soft">
                      {t("statementWorkspace.archived")}
                    </span>
                  ) : null}
                </Link>
              ) : (
                "—"
              )}
            </Td>
            <Td className="font-mono tabular text-xs text-ink-soft">
              {formatDate(line.date)}
            </Td>
            <Td className="font-mono tabular text-xs text-ink-soft">
              {formatDate(line.deliveryDate)}
            </Td>
            <Td className="max-w-56 whitespace-normal! text-xs">
              {line.paymentTerms || "—"}
            </Td>
            <Td className="font-mono tabular text-xs">
              {formatDate(line.dueAt)}
            </Td>
            <Td align="right" className="font-mono tabular">
              {formatMoney(line.total, line.currency)}
            </Td>
            <Td align="right" className="font-mono tabular">
              {formatMoney(line.paid, line.currency)}
            </Td>
            {showPeriod ? (
              <>
                <Td align="right" className="font-mono tabular">
                  {formatMoney(line.openingBalance, line.currency)}
                </Td>
                <Td align="right" className="font-mono tabular">
                  {formatMoney(line.periodBilled, line.currency)}
                </Td>
                <Td align="right" className="font-mono tabular">
                  {formatMoney(line.periodPaid, line.currency)}
                </Td>
                <Td align="right" className="font-mono tabular">
                  {formatMoney(line.periodAdjustments, line.currency)}
                </Td>
              </>
            ) : null}
            <Td align="right" className="font-mono tabular font-semibold">
              {formatMoney(line.balance, line.currency)}
            </Td>
            <Td>
              <Settlement line={line} />
            </Td>
            <Td>
              {line.balance <= 0 ? (
                <span className="text-xs text-ink-faint">
                  {t("statementWorkspace.notApplicable")}
                </span>
              ) : line.dueAt == null ? (
                <Badge tone="neutral">
                  {t("statementWorkspace.buckets.unknown")}
                </Badge>
              ) : (line.overdueDays ?? 0) > 0 ? (
                <Badge tone={(line.overdueDays ?? 0) > 60 ? "danger" : "warn"}>
                  {t("statement.overdueDays", { count: line.overdueDays ?? 0 })}
                </Badge>
              ) : (
                <Badge tone="ok">
                  {t("statementWorkspace.buckets.current")}
                </Badge>
              )}
            </Td>
            <Td className="max-w-64 whitespace-normal! text-xs text-ink-soft">
              {line.historyUncertain ? (
                <p>{t("statementWorkspace.lineHistoryLimited")}</p>
              ) : null}
              {line.dueReason ? (
                <p>
                  {t(
                    `statementWorkspace.dueReasons.${["missingTerms", "unknownTerms", "missingDelivery", "statementTermsReview"].includes(line.dueReason) ? line.dueReason : "unknownTerms"}`,
                  )}
                </p>
              ) : null}
              {!line.historyUncertain && !line.dueReason ? "—" : null}
            </Td>
          </Tr>
        ))}
      </tbody>
    </TableShell>
  );
}

export function StatementPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = searchParams.get("customerId") ?? "";
  const currency = searchParams.get("currency") ?? "";
  const q = searchParams.get("q") ?? "";
  const mode =
    searchParams.get("mode") === "asOf" ||
    (!searchParams.has("mode") && searchParams.has("to"))
      ? "asOf"
      : "current";
  const asOfDay = searchParams.get("asOf") ?? searchParams.get("to") ?? "";
  const fromDay = searchParams.get("from") ?? "";
  const filterValue =
    searchParams.get("filter") ??
    (searchParams.get("outstanding") === "1" ? "outstanding" : "all");
  const filter = FILTERS.find((value) => value === filterValue) ?? "all";
  const sort =
    SORTS.find((value) => value === searchParams.get("sort")) ?? "-date";
  const asOf = mode === "asOf" ? dateValue(asOfDay, true) : undefined;
  const today = formatDate(Date.now());
  const monthOptions = useMemo(() => statementMonthOptions(today), [today]);
  const selectedMonth = mode === "asOf" ? statementMonthOf(fromDay, asOfDay, today) : "";
  const from = dateValue(fromDay);
  const invalidDates =
    (mode === "asOf" && asOf == null) ||
    (!!fromDay && from == null) ||
    (from != null && from > (asOf ?? Date.now())) ||
    (mode === "asOf" && asOfDay > formatDate(Date.now()));
  const update = (key: string, value: string, replace = false) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete("outstanding");
        next.delete("to");
        return next;
      },
      { replace },
    );
  const searchInput = useSearchDraft(q, (value) => update("q", value, true));
  const customers = useQuotationCustomerOptions();
  const settings = useSettings();
  const report = useStatementReport(
    {
      mode,
      asOf,
      from,
      customerId: customerId || undefined,
      currency: currency || undefined,
      q: q || undefined,
      filter,
      sort,
      tzOffsetMinutes: new Date().getTimezoneOffset(),
    },
    !invalidDates,
  );
  // Previous query data must never appear under a new filter or leak into an export.
  const ready =
    !invalidDates && !report.isFetching && !report.error && !!report.data;
  const groups = ready ? report.data!.data : [];
  const meta = ready ? report.data!.meta : undefined;
  const customerOptions = (customers.data ?? []).map((c) => ({
    value: c.id,
    label: c.companyName,
    sublabel: c.address,
  }));
  if (customerId && !customerOptions.some((c) => c.value === customerId))
    customerOptions.push({
      value: customerId,
      label:
        groups.find((g) => g.customerId === customerId)?.customerName ??
        t("statementWorkspace.unknownCustomer"),
      sublabel: null,
    });
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [preview, setPreview] = useState<{
    groups: StatementDto[];
    meta: StatementReportMeta;
    html: string;
  } | null>(null);
  const [outputError, setOutputError] = useState(false);
  const byCurrency = useMemo(() => {
    const result = new Map<string, StatementDto[]>();
    for (const group of groups) {
      const existing = result.get(group.currency);
      if (existing) existing.push(group);
      else result.set(group.currency, [group]);
    }
    return [...result.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [groups]);
  const allCollapsed =
    groups.length > 0 &&
    groups.every((group) => collapsed.has(groupKey(group)));
  const toggleGroup = (key: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Bản in là đồng bộ, tệp Excel là bất đồng bộ (ghi workbook): bắt cả hai loại lỗi ở một chỗ.
  const output = (action: () => void | Promise<void>) => {
    setOutputError(false);
    Promise.resolve()
      .then(action)
      .catch(() => setOutputError(true));
  };
  const reportAvailable = ready && groups.length > 0;
  return (
    <PageBody className="h-full min-h-0 overflow-y-auto overscroll-contain">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl text-sm text-ink-soft">
          {t("statementWorkspace.intro")}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!reportAvailable}
            onClick={() => {
              if (meta)
                output(() =>
                  setPreview({
                    groups,
                    meta,
                    html: buildStatementHtml(groups, meta),
                  }),
                );
            }}
          >
            <Eye className="size-4" />
            {t("statementWorkspace.preview")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!reportAvailable}
            onClick={() => {
              if (meta) output(() => printStatement(groups, meta));
            }}
          >
            <Printer className="size-4" />
            {t("common.print")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!reportAvailable}
            onClick={() => {
              if (meta) output(() => exportStatementWorkbook(groups, meta, t));
            }}
          >
            <Download className="size-4" />
            {t("statement.exportSheets")}
          </Button>
        </div>
      </div>
      <Panel className="mt-4 p-4">
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <FieldLabel>{t("common.customer")}</FieldLabel>
            <SearchableFilter
              label={t("common.customer")}
              value={customerId}
              allLabel={t("statement.allCustomers")}
              searchPlaceholder={t("common.search")}
              options={customerOptions}
              onChange={(value) => update("customerId", value)}
              isLoading={customers.isLoading}
              className="w-full"
            />
          </div>
          <div>
            <FieldLabel htmlFor="statement-currency">
              {t("common.currency")}
            </FieldLabel>
            <Select
              id="statement-currency"
              value={currency}
              onChange={(event) => update("currency", event.target.value)}
            >
              <option value="">{t("statementWorkspace.allCurrencies")}</option>
              {[
                ...new Set(
                  [
                    ...CURRENCIES,
                    ...(settings.data?.businessOperations.optionLists
                      .currencies ?? []),
                    ...groups.map((g) => g.currency),
                    currency,
                  ].filter(Boolean),
                ),
              ].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="statement-filter">
              {t("statementWorkspace.filter")}
            </FieldLabel>
            <Select
              id="statement-filter"
              value={filter}
              onChange={(event) => update("filter", event.target.value)}
            >
              {FILTERS.map((value) => (
                <option key={value} value={value}>
                  {t(`statementWorkspace.filters.${value}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="statement-search">
              {t("statementWorkspace.search")}
            </FieldLabel>
            <Input
              id="statement-search"
              type="search"
              maxLength={200}
              leading={<Search />}
              placeholder={t("statementWorkspace.searchHint")}
              {...searchInput}
            />
          </div>
          {/* Mục 10 (Roary 04/11/2026): chọn nhanh một tháng — điền sẵn Đầu kỳ ngày 1 và Công nợ đến cuối tháng. */}
          <div>
            <FieldLabel htmlFor="statement-month">
              {t("statementWorkspace.month")}
            </FieldLabel>
            <Select
              id="statement-month"
              value={selectedMonth}
              onChange={(event) =>
                setSearchParams((previous) => {
                  const next = new URLSearchParams(previous);
                  next.delete("outstanding");
                  next.delete("to");
                  const range = statementMonthRange(event.target.value, today);
                  if (range) {
                    next.set("mode", "asOf");
                    next.set("from", range.from);
                    next.set("asOf", range.asOf);
                  } else {
                    next.delete("mode");
                    next.delete("from");
                    next.delete("asOf");
                  }
                  return next;
                })
              }
            >
              <option value="">{t("statementWorkspace.monthNone")}</option>
              {monthOptions.map((m) => (
                <option key={m.value} value={m.value}>
                  {t("statementWorkspace.monthOption", { month: m.month, year: m.year })}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="statement-mode">
              {t("statementWorkspace.mode")}
            </FieldLabel>
            <Select
              id="statement-mode"
              value={mode}
              onChange={(event) =>
                setSearchParams((previous) => {
                  const next = new URLSearchParams(previous);
                  next.set("mode", event.target.value);
                  if (event.target.value === "asOf" && !next.get("asOf"))
                    next.set("asOf", formatDate(Date.now()));
                  return next;
                })
              }
            >
              <option value="current">
                {t("statementWorkspace.currentMode")}
              </option>
              <option value="asOf">{t("statementWorkspace.asOfMode")}</option>
            </Select>
          </div>
          {mode === "asOf" ? (
            <div>
              <FieldLabel htmlFor="statement-asof">
                {t("statementWorkspace.asOfDate")}
              </FieldLabel>
              <Input
                id="statement-asof"
                type="date"
                max={formatDate(Date.now())}
                value={asOfDay}
                invalid={invalidDates}
                onChange={(event) => update("asOf", event.target.value)}
              />
            </div>
          ) : null}
          <div>
            <FieldLabel htmlFor="statement-from">
              {t("statementWorkspace.fromDate")}
            </FieldLabel>
            <Input
              id="statement-from"
              type="date"
              max={mode === "asOf" ? asOfDay : formatDate(Date.now())}
              value={fromDay}
              invalid={invalidDates}
              onChange={(event) => update("from", event.target.value)}
            />
          </div>
          <div>
            <FieldLabel htmlFor="statement-sort">
              {t("statementWorkspace.sort")}
            </FieldLabel>
            <Select
              id="statement-sort"
              value={sort}
              onChange={(event) => update("sort", event.target.value)}
            >
              {SORTS.map((value) => (
                <option key={value} value={value}>
                  {t(
                    `statementWorkspace.sorts.${value === "-date" ? "newest" : value === "-balance" ? "balance" : value === "due" ? "due" : "oldest"}`,
                  )}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="justify-self-start"
            onClick={() => setSearchParams({})}
          >
            <RotateCcw className="size-3.5" />
            {t("statementWorkspace.reset")}
          </Button>
        </div>
        <p className="mt-3 text-xs text-ink-soft">
          {t("statementWorkspace.periodHelp")}
        </p>
        {filter !== "all" || q ? (
          <p className="mt-2 text-xs text-info">
            {t("statementWorkspace.filteredScope")}
          </p>
        ) : null}
        {customers.error ? (
          <div
            role="alert"
            className="mt-3 flex flex-wrap items-center gap-2 text-xs text-danger"
          >
            {t("statementWorkspace.customerError")}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void customers.refetch()}
            >
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
      </Panel>
      {outputError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {t("statementWorkspace.outputError")}
        </p>
      ) : null}
      {invalidDates ? (
        <p
          role="alert"
          className="mt-4 rounded-sm border border-danger/30 bg-danger-tint p-4 text-sm text-danger"
        >
          {t("statementWorkspace.invalidDates")}
        </p>
      ) : report.error ? (
        <div className="mt-4">
          <ErrorState
            message={t("statement.errorLoad")}
            onRetry={() => void report.refetch()}
          />
        </div>
      ) : !ready ? (
        <div role="status" aria-live="polite">
          <LoadingState label={t("statementWorkspace.loading")} />
        </div>
      ) : meta ? (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-soft">
            <span>
              {t("statementWorkspace.reportAt", {
                at: formatDateTime(meta.asOf, i18n.language),
              })}
            </span>
            <span>
              {t("statementWorkspace.generatedAt", {
                at: formatDateTime(meta.generatedAt, i18n.language),
              })}
            </span>
          </div>
          <div className="mt-3 rounded-sm border border-warn/30 bg-warn-tint p-3 text-xs leading-relaxed text-ink-soft">
            <p className="font-semibold text-ink">
              {t("statementWorkspace.basisTitle")}
            </p>
            <p>
              {t(
                meta.mode === "asOf" || meta.from != null
                  ? "statementWorkspace.historyBasis"
                  : "statementWorkspace.currentBasis",
              )}
            </p>
            <p className="mt-1">{t("statementWorkspace.dueBasis")}</p>
            <p>{t("statementWorkspace.roundingBasis")}</p>
            {meta.historyLimited ? (
              <p className="mt-1 font-medium">
                {t("statementWorkspace.historyLimited")}
              </p>
            ) : null}
          </div>
          {groups.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                title={t("statementWorkspace.empty")}
                hint={t("statementWorkspace.emptyHint")}
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setSearchParams({})}
                  >
                    {t("statementWorkspace.reset")}
                  </Button>
                }
              />
            </div>
          ) : (
            <>
              <div className="mt-4 space-y-4">
                {byCurrency.map(([cur, currencyGroups]) => {
                  const sum = (
                    key:
                      | "receivable"
                      | "credit"
                      | "totalBalance"
                      | "overdueAmount"
                      | "openingBalance"
                      | "periodBilled"
                      | "periodPaid"
                      | "periodAdjustments"
                      | "unagedAmount",
                  ) =>
                    currencyGroups.reduce(
                      (total, group) => total + group[key],
                      0,
                    );
                  return (
                    <Panel key={cur} className="p-4">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h2 className="font-mono tabular text-sm font-semibold text-ink">
                          {cur}
                        </h2>
                        <span className="text-xs text-ink-soft">
                          {t("statementWorkspace.groupCount", {
                            count: currencyGroups.length,
                          })}
                        </span>
                      </div>
                      <dl className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                        {(
                          [
                            "receivable",
                            "credit",
                            "totalBalance",
                            "overdueAmount",
                          ] as const
                        ).map((key) => (
                          <div key={key}>
                            <dt className="eyebrow">
                              {t(`statementWorkspace.totals.${key}`)}
                            </dt>
                            <dd
                              className={`mt-1 font-mono tabular text-xl font-semibold ${key === "overdueAmount" ? "text-danger" : "text-ink"}`}
                            >
                              {formatMoney(sum(key), cur)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                      <p className="mt-3 text-xs text-ink-soft">
                        {t("statementWorkspace.netHelp")}
                      </p>
                      {meta.from != null ? (
                        <div className="mt-4 border-t border-line pt-3">
                          <p className="mb-2 text-xs text-ink-soft">
                            {t("statementWorkspace.periodEquation")}
                          </p>
                          <p className="mb-3 text-xs text-ink-soft">
                            {t("statementWorkspace.adjustmentsHelp")}
                          </p>
                          <dl className="grid grid-cols-2 gap-3 md:grid-cols-5">
                            {(
                              [
                                "openingBalance",
                                "periodBilled",
                                "periodPaid",
                                "periodAdjustments",
                                "totalBalance",
                              ] as const
                            ).map((key) => (
                              <div key={key}>
                                <dt className="text-xs text-ink-soft">
                                  {t(`statementWorkspace.periodTotals.${key}`)}
                                </dt>
                                <dd className="mt-1 font-mono tabular text-sm">
                                  {formatMoney(sum(key), cur)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      ) : null}
                      <div className="mt-4 border-t border-line pt-3">
                        <p className="eyebrow mb-3">
                          {t("statement.agingAll")} ·{" "}
                          <span className="font-mono">{cur}</span>
                        </p>
                        <Aging groups={currencyGroups} currency={cur} />
                        <p className="mt-3 text-xs text-ink-soft">
                          {t("statementWorkspace.unagedHelp", {
                            amount: formatMoney(sum("unagedAmount"), cur),
                          })}
                        </p>
                      </div>
                    </Panel>
                  );
                })}
              </div>
              <div className="mb-3 mt-5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-ink">
                  {t("statementWorkspace.documents", {
                    count: groups.reduce(
                      (count, group) => count + group.lines.length,
                      0,
                    ),
                  })}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setCollapsed(
                      allCollapsed ? new Set() : new Set(groups.map(groupKey)),
                    )
                  }
                >
                  {t(
                    allCollapsed
                      ? "statement.expandAll"
                      : "statement.collapseAll",
                  )}
                </Button>
              </div>
              <div className="space-y-4">
                {groups.map((group) => {
                  const key = groupKey(group);
                  const isCollapsed = collapsed.has(key);
                  return (
                    <Panel key={key} className="overflow-hidden">
                      <button
                        type="button"
                        className="flex w-full flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3 text-left hover:bg-row-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
                        onClick={() => toggleGroup(key)}
                        aria-expanded={!isCollapsed}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <ChevronDown
                            aria-hidden="true"
                            className={`size-4 shrink-0 ${isCollapsed ? "-rotate-90" : ""}`}
                          />
                          <CompanyTag region={group.customerRegion} />
                          <span className="font-semibold text-ink">
                            {group.customerName ||
                              t("statementWorkspace.unknownCustomer")}
                          </span>
                          <Badge>{group.currency}</Badge>
                        </span>
                        <span className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-ink-soft">
                          <span>
                            {t("statementWorkspace.totals.receivable")}{" "}
                            <span className="font-mono tabular">
                              {formatMoney(group.receivable, group.currency)}
                            </span>
                          </span>
                          <span>
                            {t("statementWorkspace.totals.credit")}{" "}
                            <span className="font-mono tabular">
                              {formatMoney(group.credit, group.currency)}
                            </span>
                          </span>
                          <span>
                            {t("statementWorkspace.netBalance")}{" "}
                            <strong className="font-mono tabular text-sm text-ink">
                              {formatMoney(group.totalBalance, group.currency)}
                            </strong>
                          </span>
                        </span>
                      </button>
                      {!isCollapsed ? (
                        <>
                          <div className="p-4">
                            <Aging groups={[group]} currency={group.currency} />
                          </div>
                          <StatementLines
                            group={group}
                            showPeriod={meta.from != null}
                          />
                        </>
                      ) : null}
                    </Panel>
                  );
                })}
              </div>
            </>
          )}
        </>
      ) : null}
      {preview ? (
        <Modal
          title={t("statementWorkspace.preview")}
          closeLabel={t("common.close")}
          onClose={() => setPreview(null)}
          className="max-w-6xl"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line p-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="text-xs text-ink-soft">
                {t("statementWorkspace.previewSnapshot", {
                  at: formatDateTime(preview.meta.generatedAt, i18n.language),
                })}
              </p>
              <SaveAsPdfHint />
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!ready}
                onClick={() =>
                  output(() => printStatement(preview.groups, preview.meta))
                }
              >
                <Printer className="size-4" />
                {t("common.print")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!ready}
                onClick={() =>
                  output(() =>
                    exportStatementWorkbook(preview.groups, preview.meta, t),
                  )
                }
              >
                <Download className="size-4" />
                {t("statement.exportSheets")}
              </Button>
            </div>
          </div>
          {outputError ? (
            <p role="alert" className="p-3 text-sm text-danger">
              {t("statementWorkspace.outputError")}
            </p>
          ) : null}
          <iframe
            title={t("statementWorkspace.previewFrame")}
            srcDoc={preview.html}
            sandbox="allow-same-origin"
            className="w-full shrink-0 border-0 bg-surface"
            style={{ height: "65dvh", minHeight: "65dvh" }}
          />
        </Modal>
      ) : null}
    </PageBody>
  );
}
